/**
 * Pure-function deep-link option builder for the per-tile chip click.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The chip
 * click in `<Item>` passes `{ defaultPanel: Panel.IrisConnection,
 * defaultTab: chipState.targetTab }` to
 * `handler.onSettings(device.id, options)` so the advanced-settings
 * dialog opens at the sub-tab diagnosed by the chip's verb.
 *
 * Per `think-in-systems` (ai.knowledge/global): the option-building
 * logic is extracted to a pure function so it can be unit-tested
 * without rendering the full `<Item>` graph (the rest of `<Item>`'s
 * dependencies — proxy, livekit, locateDevice — are heavy).
 *
 * Returns `undefined` when `chipState` is null — the chip doesn't
 * render in that case, so the call site falls back to the legacy
 * argument-less call (preserves existing context-menu / Settings
 * single-add behavior).
 */
import type { ChipState } from './chip-state.js';
import { Panel, type IrisConnectionTab } from '#~/video/components/settings/store/index.js';

/** Settings handler options derived from a chip-state. */
export type ChipClickOptions = {
  defaultPanel: Panel;
  defaultTab: IrisConnectionTab;
};

/**
 * Build the deep-link options for the chip click.
 *
 * @param chipState - Derived chip state (null when no chip renders).
 * @returns Options when `chipState` is non-null; otherwise `undefined`.
 */
export const chipClickOptions = (chipState: ChipState | null): ChipClickOptions | undefined => {
  if (chipState === null) {
    return undefined;
  }
  return {
    defaultPanel: Panel.IrisConnection,
    defaultTab: chipState.targetTab
  };
};
